import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTheme } from '../../styles/ThemeProvider';
import {
  FilterBar, DateRangePicker, ExportButton, KpiCard, ChartCard, DataTable,
  CategoryBar,
} from '../../components/analytics';
import { formatTRY, formatPct, formatCompact } from '../../utils/analytics/format';
import { exportPdf } from '../../utils/analytics/export';
import { rangeFromUrl } from '../../utils/analytics/dateRange';
import { useAnalyticsQuery } from '../../hooks/useAnalyticsQuery';

// Kâr bandı grafiği Plotly ile çizilir.
import Plotly from 'plotly.js-dist-min';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const authHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const abcBadge = (cls, theme) => {
  const map = {
    A: { bg: theme.color.success, label: 'A' },
    B: { bg: theme.color.warning, label: 'B' },
    C: { bg: theme.color.muted, label: 'C' },
  };
  const m = map[cls] || map.C;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 12,
      background: m.bg, color: '#fff', fontSize: 11, fontWeight: 600,
    }}>{m.label}</span>
  );
};

export default function MenuKarliligiPage({ embedded = false }) {
  const { theme } = useTheme();
  const plotRef = useRef(null);
  // Cockpit drill-down bağlamı: dönem URL'den; firma ise normalize-eşleştirilerek
  // sayfanın marka filtresine uygulanır (eşleşmezse tarih-only — boş sayfa olmaz,
  // çünkü Recipe.marka deprecated string ile firma kodu birebir tutmayabilir).
  const [searchParams] = useSearchParams();
  const [range, setRange] = useState(() => rangeFromUrl(searchParams));
  const [marka, setMarka] = useState('');
  const urlFirma = searchParams.get('firma') || '';
  const firmaAppliedRef = useRef(false);
  const containerRef = useRef(null);

  const params = useMemo(() => {
    const p = new URLSearchParams();
    p.set('baslangic', range.start);
    p.set('bitis', range.end);
    if (marka) p.set('marka', marka);
    return p.toString();
  }, [range, marka]);

  const { data, loading, error } = useAnalyticsQuery(
    `menu-profit:${params}`,
    async ({ signal }) => {
      const res = await fetch(`${API}/api/analytics/satis/menu-profitability?${params}`, { signal, headers: authHeaders() });
      if (!res.ok) throw new Error(`Hata ${res.status}`);
      return res.json();
    },
    [params],
  );

  const rows = useMemo(() => Array.isArray(data) ? data : [], [data]);

////////////////////   EKLEME   ///////////////////////////////////
    //
    const aiTextRef = useRef(null);
    const yorumUret = (p) => {
      const urun = p.text || "Bu ürün";
      const adet = Number(p.x || 0);
      const birimKar = Number(p.y || 0);
      const toplamKar = Number(p.customdata?.[1] || 0);
    
      if (adet >= 2500 && birimKar >= 250) {
        return `${urun}: Çok güçlü ürün. Satış adedi yüksek, birim kârı da iyi. Menüde görünürlüğü korunmalı; stok ve üretim sürekliliği özellikle takip edilmeli.`;
      }
    
      if (adet >= 2500 && birimKar < 150) {
        return `${urun}: Çok satıyor fakat birim kâr düşük. Fiyat, porsiyon maliyeti ve reçete maliyeti kontrol edilmeli. Küçük fiyat artışı veya maliyet revizyonu kârlılığı ciddi artırabilir.`;
      }
    
      if (adet < 800 && birimKar >= 250) {
        return `${urun}: Kârlı ama satış adedi düşük. Menüde daha görünür yapılabilir, personel önerisiyle desteklenebilir veya kampanya içinde öne çıkarılabilir.`;
      }
    
      if (adet < 800 && birimKar < 150) {
        return `${urun}: Hem satış adedi hem birim kâr düşük görünüyor. Menüden çıkarma, reçete sadeleştirme, fiyat güncelleme veya porsiyon revizyonu değerlendirilebilir.`;
      }
    
      if (toplamKar >= 100000) {
        return `${urun}: Toplam kâr katkısı yüksek. Bu ürün işletme kârlılığına güçlü katkı sağlıyor. Operasyonel aksama, stok eksikliği veya kalite dalgalanması yaşanmaması önemli.`;
      }
    
      return `${urun}: Dengeli performans gösteriyor. Satış adedi, birim kâr ve toplam katkı düzenli takip edilmeli. Menüdeki konumu korunabilir.`;
    };
    //
    const tumUrunler = useMemo(() => {
      const liste = rows
        .filter(r => r.ad && (r.satis_adet || 0) > 0)
        .map(r => ({
          urun: r.ad,
          firma: r.marka || '',
          adet: Number(r.satis_adet || 0),
          toplam_katki: Number(r.brut_kar || 0),
          birim_katki:
            Number(r.satis_adet || 0) > 0
              ? Number(r.brut_kar || 0) / Number(r.satis_adet)
              : 0
        }))
        .sort((a, b) => b.toplam_katki - a.toplam_katki);
    
      return liste;
    }, [rows]);
    
    const [seciliUrunler, setSeciliUrunler] = useState([]);
    //const plotRef = useRef(null);

    useEffect(() => {
      if (tumUrunler.length > 0 && seciliUrunler.length === 0) {
        setSeciliUrunler(tumUrunler.slice(0, 10).map((x) => x.urun));
      }
    }, [tumUrunler, seciliUrunler.length]);
    
    const grafikSatirlari = useMemo(() => {
      return tumUrunler.filter((x) => seciliUrunler.includes(x.urun));
    }, [tumUrunler, seciliUrunler]);

    useEffect(() => {
      if (!plotRef.current || grafikSatirlari.length === 0) return;
    
      const maxAdet = Math.max(...grafikSatirlari.map((x) => x.adet), 1);
      const maxKar = Math.max(...grafikSatirlari.map((x) => x.birim_katki), 1);
    
      const karDegerleri = [
        30000, 60000, 90000, 120000, 150000, 180000,
        210000, 240000, 270000, 300000, 330000, 360000,390000, 420000
      ];
    
      const karBandlari = karDegerleri.map((kar) => {
        const xDegerleri = [];
        const yDegerleri = [];
    
        for (let i = 1; i <= 140; i++) {
          const adet = 1 + (i * maxAdet) / 140;
          const birimKar = kar / adet;
    
          if (birimKar <= maxKar * 1.2) {
            xDegerleri.push(adet);
            yDegerleri.push(birimKar);
          }
        }
    
        return {
          x: xDegerleri,
          y: yDegerleri,
          type: 'scatter',
          mode: 'lines',
          name: `${kar.toLocaleString('tr-TR')} TL`,
          line: {
            color: '#facc15',
            width: 2,
            dash: 'dash',
          },
          hovertemplate: `${kar.toLocaleString('tr-TR')} TL<extra></extra>`,
        };
      });
    
      const urunTrace = {
        x: grafikSatirlari.map((x) => x.adet),
        y: grafikSatirlari.map((x) => x.birim_katki),
        text: grafikSatirlari.map((x) => x.urun),
        customdata: grafikSatirlari.map((x) => [
          x.firma,
          x.toplam_katki,
        ]),
        type: 'scatter',
        mode: 'markers+text',
        textposition: 'top center',
        marker: {
          size: 28,
          color: '#2563eb',
          opacity: 0.85,
          line: {
            color: '#ffffff',
            width: 1,
          },
        },
        hovertemplate:
          '<b>%{text}</b><br>' +
          'Firma: %{customdata[0]}<br>' +
          'Satış Adedi: %{x:,.0f}<br>' +
          'Birim Kâr: %{y:,.2f} TL<br>' +
          'Toplam Kâr: %{customdata[1]:,.2f} TL' +
          '<extra></extra>',
      };
    
        Plotly.react(
          plotRef.current,
          [...karBandlari, urunTrace],
          {
            title: 'Kâr Bandı Grafiği',
            height: 530,
            margin: { l: 120, r: 40, t: 60, b: 100 },
            xaxis: {
              title: { text: 'Satış Adedi' },
              automargin: true,
              showline: true,
              linecolor: '#444',
              linewidth: 2,
              mirror: false,
              showgrid: true,
              zeroline: false,
              range: [0, maxAdet * 1.15],
            },
            yaxis: {
              title: { text: 'Birim Kâr / Katkı (TL)' },
              automargin: true,
              showline: true,
              linecolor: '#444',
              linewidth: 2,
              mirror: false,
              showgrid: true,
              zeroline: false,
              range: [0, maxKar * 1.25],
            },
            showlegend: false,
            hovermode: 'closest',
          },
          {
            responsive: true,
            displaylogo: false,
            scrollZoom: true,
          }
            );
            
            plotRef.current.removeAllListeners("plotly_hover");
            
            plotRef.current.on("plotly_hover", (event) => {
              const p = event.points?.[0];
              if (!p || !p.text || !aiTextRef.current) return;
            
              aiTextRef.current.innerText = yorumUret(p);
            });

  }, [grafikSatirlari]);
 //////////////////////////////////////////////////////////////////// 

  const totals = useMemo(() => {
    const adet = rows.reduce((s, r) => s + (r.satis_adet || 0), 0);
    const ciro = rows.reduce((s, r) => s + (r.satis_ciro || 0), 0);
    // weighted avg food cost
    const wfc = rows.reduce((s, r) => (r.food_cost_pct != null && r.satis_ciro)
      ? s + r.food_cost_pct * r.satis_ciro : s, 0);
    const avgFc = ciro > 0 ? wfc / ciro : null;
    // top profit
    const top = rows.reduce((best, r) => (r.brut_kar || 0) > (best?.brut_kar || -Infinity) ? r : best, null);
    return { adet, ciro, avgFc, top };
  }, [rows]);
 
 //////////////////////////////////////////////////////////////////// 
 
  // Marka seçenekleri doğrudan veriden (DB) türetilir — hardcode marka adı yok.
  const markaOptions = useMemo(() => {
    const set = new Set();
    rows.forEach(r => { if (r.marka) set.add(r.marka); });
    return [...set].sort();
  }, [rows]);

 //////////////////////////////////////////////////////////////////// 

  // Drill'den gelen firma'yı (ör. INGA_BURGER) mevcut marka seçenekleriyle bir kez
  // normalize-eşleştir (büyük/küçük + _/boşluk farkını yok say). Eşleşirse marka
  // filtresini uygula; eşleşmezse hiç dokunma (tarih bağlamı yine korunur).
  useEffect(() => {
    if (firmaAppliedRef.current || !urlFirma || marka || !markaOptions.length) return;
    const norm = (x) => (x || '').toString().toUpperCase().replace(/[\s_]/g, '');
    const match = markaOptions.find(o => norm(o) === norm(urlFirma));
    if (match) setMarka(match);
    firmaAppliedRef.current = true; // tek deneme
  }, [markaOptions, urlFirma, marka]);

  const top10Kar = useMemo(() =>
    [...rows].sort((a, b) => (b.brut_kar || 0) - (a.brut_kar || 0)).slice(0, 10).map(r => ({
      label: (r.ad || '').slice(0, 30), value: r.brut_kar || 0,
    })),
    [rows]);

  const top10Fc = useMemo(() =>
    [...rows].filter(r => r.food_cost_pct != null).sort((a, b) => b.food_cost_pct - a.food_cost_pct).slice(0, 10)
      .map(r => ({ label: (r.ad || '').slice(0, 30), value: r.food_cost_pct })),
    [rows]);

  const onPdf = () => {
    if (containerRef.current) exportPdf(containerRef.current, `menu-karliligi-${range.start}_${range.end}`);
  };

  const Wrapper = ({ children }) =>
    embedded ? (
      <div style={{ display: 'flex', flexDirection: 'column', gap: theme.space.lg, fontFamily: theme.font.family }}>
        {children}
      </div>
    ) : (
      <div ref={containerRef} style={{
        padding: theme.space.lg, display: 'flex', flexDirection: 'column', gap: theme.space.lg,
        background: theme.color.surfaceAlt, minHeight: '100vh', fontFamily: theme.font.family,
      }}>{children}</div>
    );

  const selectStyle = {
    padding: `${theme.space.xs}px ${theme.space.md}px`,
    borderRadius: theme.radius.sm, border: `1px solid ${theme.color.border}`,
    background: theme.color.surface, color: theme.color.text,
    fontSize: theme.font.size.xs, fontFamily: theme.font.family,
  };

  return (
    <Wrapper>
      {!embedded && (
        <header>
          <h1 style={{ margin: 0, color: theme.color.text, fontSize: theme.font.size.xxl, fontWeight: theme.font.weight.bold }}>
            Satış & Menü Kârlılığı
          </h1>
          <div style={{ color: theme.color.textSoft, fontSize: theme.font.size.sm, marginTop: 4 }}>
            POS satışları × reçete maliyeti — ürün bazlı brüt kâr ve ABC sınıflandırma
          </div>
        </header>
      )}

      <FilterBar sticky={!embedded}>
        <DateRangePicker value={range} onChange={setRange} />
        <select value={marka} onChange={(e) => setMarka(e.target.value)} style={selectStyle}>
          <option value="">Tüm Markalar</option>
          {markaOptions.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
        <div style={{ marginLeft: 'auto' }}>
          <ExportButton onExportPdf={onPdf} />
        </div>
      </FilterBar>

      {error && <div style={{ color: theme.color.danger }}>{`Hata: ${error}`}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: theme.space.base }}>
        <KpiCard title="Toplam Satış Adedi" value={loading ? undefined : totals.adet.toLocaleString('tr-TR')} loading={loading} />
        <KpiCard title="Toplam Ciro" value={loading ? undefined : formatTRY(totals.ciro)} loading={loading} />
        <KpiCard
          title="Ort. Food Cost"
          value={loading ? undefined : (totals.avgFc == null ? '—' : formatPct(totals.avgFc))}
          increaseIsBad
          loading={loading}
        />
        <KpiCard
          title="En Kârlı Ürün"
          value={loading ? undefined : (totals.top ? totals.top.ad : '—')}
          deltaLabel={totals.top ? formatTRY(totals.top.brut_kar) : ''}
          delta={totals.top ? 0 : undefined}
          loading={loading}
        />
      </div>

{/* /////////////////////// EKLENEN GRAFİK //////////////////////////////// */}


    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '280px 1fr',
        gap: 16,
        marginTop: 16,
      }}
    >
      <div>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>
          Ürün Seçimi
        </div>
    
        <select
          multiple
          value={seciliUrunler}
          onChange={(e) =>
            setSeciliUrunler(
              Array.from(e.target.selectedOptions).map((o) => o.value)
            )
          }
          style={{
            width: '100%',
            height: 500,
            padding: 8,
          }}
        >
          {tumUrunler.map((x) => (
            <option key={x.urun} value={x.urun}>
              {x.urun}
            </option>
          ))}
        </select>
      </div>
    
      <div
        style={{
          border: '1px solid #ddd',
          borderRadius: 8,
          minHeight: 420,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        
        <div ref={plotRef} style={{ width: '100%', height: 530 }} />
      </div>
    </div>
        <div
              ref={aiTextRef}
              style={{
                marginTop: 12,
                padding: 14,
                border: '1px solid #e5e7eb',
                borderRadius: 10,
                background: '#f8fafc',
                fontSize: 14,
                lineHeight: 1.6,
                color: '#111827',
              }}
            >
              Grafikte bir ürünün üzerine gelerek ürün yorumunu görebilirsiniz.
            </div>


{/* ////////////////////////////////////////////////////////////////////////// */}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: theme.space.base }}>
        <ChartCard title="Top 10 Kârlılık (Brüt Kâr)" height={280}
                   loading={loading} empty={!loading && top10Kar.length === 0}>
          <CategoryBar data={top10Kar} formatter={formatCompact} />
        </ChartCard>

        <ChartCard title="Top 10 Yüksek Food Cost" subtitle="Dikkat gerektiren ürünler" height={280}
                   loading={loading} empty={!loading && top10Fc.length === 0}>
          <CategoryBar data={top10Fc} formatter={(v) => `%${v.toFixed(1)}`} />
        </ChartCard>
      </div>

      <ChartCard title="Ürün Listesi"
                 loading={loading}
                 empty={!loading && rows.length === 0}
                 emptyLabel="Bu dönemde eşleşen POS satışı yok">
        <DataTable
          rows={rows.map(r => ({ id: r.recipe_id, ...r }))}
          columns={[
            { key: 'ad', label: 'Ürün' },
            { key: 'marka', label: 'Marka' },
            { key: 'abc_sinifi', label: 'ABC', render: (v) => abcBadge(v, theme) },
            { key: 'satis_adet', label: 'Adet', align: 'right', render: (v) => Math.round(v).toLocaleString('tr-TR') },
            { key: 'satis_ciro', label: 'Ciro', align: 'right', render: (v) => formatTRY(v) },
            { key: 'birim_maliyet', label: 'Birim Maliyet', align: 'right', render: (v) => v == null ? '—' : formatTRY(v, { decimals: 2 }) },
            { key: 'brut_kar', label: 'Brüt Kâr', align: 'right', render: (v) => formatTRY(v) },
            { key: 'food_cost_pct', label: 'Food Cost', align: 'right',
              render: (v) => v == null ? '—' : (
                <span style={{ color: v > 40 ? theme.color.danger : theme.color.text }}>{formatPct(v)}</span>
              )
            },
            { key: 'margin_pct', label: 'Marj', align: 'right',
              render: (v) => v == null ? '—' : (
                <span style={{ color: v > 20 ? theme.color.success : theme.color.text }}>{formatPct(v)}</span>
              )
            },
          ]}
          initialSort={{ key: 'satis_ciro', dir: 'desc' }}
        />
      </ChartCard>
    </Wrapper>
  );
}
